import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import { PhotographerSelectionDialog } from './PhotographerSelectionDialog';
import { Translate } from '../../../../../../../shared/LanguageService';
import { Hint } from '../../view/Hint';
import { AvailablePhotographer } from '../../types';

type PhotographerSectionLabels = {
    closePhotographerSelection: string;
    photographerSelectionDialogTitle: string;
    showPortfolio: string;
};

type IPhotographer = {
    name: string;
    profileImageReference: string;
};

type ExternalProps = {
    showHint: boolean;
    availablePhotographers: AvailablePhotographer[];
    disabled: boolean;
    onLeftClick?: () => void;
    onRightClick?: () => void;
    leftDisabled?: boolean;
    rightDisabled?: boolean;
    photographerIndicatorLabel: string;
    photographer: IPhotographer | null;
    onChange?: (photographerId: number) => void;
    onClickPortfolio?: () => void;
};

type PhotographerSectionProps = {
    showHint: boolean;
    availablePhotographers: AvailablePhotographer[];
    disabled: boolean;
    photographer: IPhotographer | null;
    onLeftClick?: () => void;
    onRightClick?: () => void;
    leftDisabled?: boolean;
    rightDisabled?: boolean;
    photographerIndicatorLabel: string;
    labels: PhotographerSectionLabels;
    onChange?: (photographerId: number) => void;
    onClickPortfolio?: () => void;
};

export function PhotographerSection({
    showHint,
    availablePhotographers,
    disabled,
    photographer,
    labels,
    onLeftClick,
    onRightClick,
    leftDisabled,
    rightDisabled,
    photographerIndicatorLabel,
    onChange,
    onClickPortfolio
}: PhotographerSectionProps) {
    let { apiUrl } = window.appConfig;
    let [showPhotographerSelection, setShowPhotographerSelection] = React.useState(false);

    function handleClosePhotographerSelection() {
        setShowPhotographerSelection(false);
    }

    function handleClickPortfolio(event: React.MouseEvent<HTMLButtonElement>) {
        event.stopPropagation();

        if (typeof onClickPortfolio === 'function') {
            onClickPortfolio();
        }
    }

    function handleChangePhotographer(photographerId: number) {
        if (typeof onChange === 'function') {
            onChange(photographerId);
        }
        setShowPhotographerSelection(false);
    }

    return (
        <>
            <Box
                component="section"
                sx={{
                    paddingBottom: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    width: '100%'
                }}
            >
                <Hint showHint={showHint}>
                    {Translate('se360/photographerbooking/customer/calendar/photographer-hint')}
                </Hint>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ flex: '1 auto' }} />
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '10px',
                            fontFamily: 'Gilroy',
                            color: 'text.secondary'
                        }}
                    >
                        {photographerIndicatorLabel}
                    </Typography>
                </Box>
                {photographer ? (
                    <Card
                        sx={{
                            display: 'flex',
                            padding: '16px',
                            justifyContent: 'space-between',
                            userSelect: 'none'
                        }}
                    >
                        <IconButton
                            disabled={disabled ? true : leftDisabled ? leftDisabled : false}
                            onClick={() => {
                                if (typeof onLeftClick === 'function') {
                                    onLeftClick();
                                }
                            }}
                        >
                            <ArrowBackIosNewIcon />
                        </IconButton>
                        <Box
                            sx={{ display: 'flex', flex: '1 auto', justifyContent: 'center' }}
                            onClick={() => {
                                if (!disabled) {
                                    setShowPhotographerSelection(true);
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '8px'
                                }}
                            >
                                <Avatar
                                    alt={`Profile image of photographer ${photographer.name}`}
                                    src={`${apiUrl}/file/${photographer.profileImageReference}`}
                                />
                                <Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: '12px',
                                            fontFamily: 'Gilroy',
                                            color: disabled ? '#aaa' : 'text.primary'
                                        }}
                                    >
                                        {photographer.name}
                                    </Typography>
                                    {disabled ? (
                                        <Typography
                                            sx={{
                                                fontSize: '1rem',
                                                color: '#aaa'
                                            }}
                                        >
                                            {labels.showPortfolio}
                                        </Typography>
                                    ) : (
                                        <Button
                                            variant="text"
                                            onClick={handleClickPortfolio}
                                            sx={{
                                                padding: 'unset',
                                                textTransform: 'unset',
                                                color: '#0B79D0',
                                                fontSize: '1rem'
                                            }}
                                        >
                                            {labels.showPortfolio}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        <IconButton
                            disabled={disabled ? true : rightDisabled ? rightDisabled : false}
                            onClick={() => {
                                if (typeof onRightClick === 'function') {
                                    onRightClick();
                                }
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Card>
                ) : (
                    <Card
                        sx={{
                            display: 'flex',
                            padding: '16px',
                            justifyContent: 'center',
                            userSelect: 'none'
                        }}
                    >
                        <Box
                            sx={{ display: 'flex', flex: '1 auto', justifyContent: 'center' }}
                            onClick={() => {
                                if (!disabled) {
                                    setShowPhotographerSelection(true);
                                }
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    fontSize: '20px'
                                }}
                            >
                                {Translate(
                                    'se360/photographerbooking/page/text/choose-photographer'
                                )}
                            </Typography>
                        </Box>
                        <IconButton
                            disabled={disabled ? true : rightDisabled ? rightDisabled : false}
                            onClick={() => {
                                if (typeof onRightClick === 'function') {
                                    onRightClick();
                                }
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Card>
                )}
            </Box>
            <PhotographerSelectionDialog
                availablePhotographers={availablePhotographers}
                onChange={handleChangePhotographer}
                onClose={handleClosePhotographerSelection}
                labels={{
                    photographerSelectionDialogTitle: Translate(
                        'se360/photographerbooking/customer/calendar/photographer-selection-dialog/title'
                    ),
                    closePhotographerSelection: Translate('cancel')
                }}
                isOpen={showPhotographerSelection}
            />
        </>
    );
}

export default function TranslatedPhotographerSection(props: ExternalProps) {
    let labels: PhotographerSectionLabels = {
        showPortfolio: Translate('calendars/header/button/portfolio'),
        closePhotographerSelection: Translate('cancel'),
        photographerSelectionDialogTitle: Translate(
            'se360/photographerbooking/customer/calendar/photographer-selection-dialog/title'
        )
    };
    return <PhotographerSection {...props} labels={labels} />;
}
